import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ArrowUpRight } from "lucide-react";

// Warn-but-allow duplicate detection: debounce-checks the prospective project name/company
// against existing leads + bids and surfaces near-matches so a duplicate isn't created.
export function SimilarProjectsWarning({ name, company, excludeLead, excludeBid, onClose }) {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    const n = (name || "").trim();
    const c = (company || "").trim();
    if (n.length < 3 && c.length < 3) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/projects/similar", {
          params: { name: n || undefined, company: c || undefined, exclude_lead: excludeLead, exclude_bid: excludeBid },
        });
        setMatches(r.data.matches || []);
      } catch { setMatches([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [name, company, excludeLead, excludeBid]);

  if (matches.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="similar-projects-warning">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
        <AlertTriangle className="w-3.5 h-3.5" />
        This may already exist — {matches.length} similar project{matches.length === 1 ? "" : "s"}
      </div>
      <div className="mt-1.5 space-y-1">
        {matches.map((m) => (
          <Link
            key={`${m.kind}:${m.id}`}
            to={m.href}
            onClick={onClose}
            data-testid={`similar-match-${m.id}`}
            className="flex items-center gap-1.5 text-xs text-amber-900 hover:text-[#003E87] group"
          >
            <ArrowUpRight className="w-3 h-3 shrink-0 opacity-60 group-hover:opacity-100" />
            <span className="truncate">{m.label}</span>
            {m.status && <span className="text-[10px] uppercase tracking-wide text-amber-600 shrink-0">· {m.status}</span>}
          </Link>
        ))}
      </div>
      <div className="text-[11px] text-amber-700 mt-1.5">Not the same project? Carry on — this is just a heads-up.</div>
    </div>
  );
}

export default SimilarProjectsWarning;
